import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  EditCheckSpec,
  CATEGORY_CONFIG,
  SEVERITY_CONFIG,
  TIMING_CONFIG,
  CHECK_TYPE_LABELS,
} from '../../constants/editCheckOptions';
import EditCheckDetail from './EditCheckDetail';
import { isDefaultRange, isFromDictionary } from '../../features/editChecks/dictionaryChecksDisplay';
import { checkSourceBadge } from '../../features/editChecks/notWrittenDisplay';

interface EditCheckTableProps {
  checks: EditCheckSpec[];
  onSaveCheck: (check: EditCheckSpec) => void;
  onToggleActive: (checkId: string) => void;
  onDeleteCheck: (checkId: string) => void;
}

type CategoryKey = keyof typeof CATEGORY_CONFIG;

const CATEGORY_COLORS: Record<string, string> = {
  purple: 'border-l-purple-500 bg-purple-50/50',
  red: 'border-l-red-500 bg-red-50/50',
  amber: 'border-l-amber-500 bg-amber-50/50',
  blue: 'border-l-blue-500 bg-blue-50/50',
};

function truncate(text: string, max: number): string {
  if (!text) return '';
  return text.length > max ? text.slice(0, max) + '...' : text;
}

export default function EditCheckTable({
  checks,
  onSaveCheck,
  onToggleActive,
  onDeleteCheck,
}: EditCheckTableProps) {
  const [expandedCheckId, setExpandedCheckId] = useState<string | null>(null);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());

  // Group by category in priority order
  const categories = Object.entries(CATEGORY_CONFIG)
    .sort(([, a], [, b]) => a.priority - b.priority)
    .map(([key]) => key as CategoryKey);

  const grouped = new Map<CategoryKey, EditCheckSpec[]>();
  for (const cat of categories) {
    const items = checks.filter((c) => c.category === cat);
    if (items.length > 0) {
      grouped.set(cat, items);
    }
  }

  const toggleCategory = (cat: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) {
        next.delete(cat);
      } else {
        next.add(cat);
      }
      return next;
    });
  };

  const handleRowClick = (checkId: string) => {
    setExpandedCheckId((prev) => (prev === checkId ? null : checkId));
  };

  if (checks.length === 0) {
    return (
      <div className="text-center py-8 text-sm text-gray-400">
        No checks match the current filters.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {categories.map((cat) => {
        const items = grouped.get(cat);
        if (!items) return null;

        const config = CATEGORY_CONFIG[cat];
        const isCollapsed = collapsedCategories.has(cat);

        return (
          <div key={cat} className="border border-gray-200 rounded-lg overflow-hidden">
            {/* Category header */}
            <button
              onClick={() => toggleCategory(cat)}
              className={`w-full flex items-center justify-between px-4 py-2.5 border-l-4 ${CATEGORY_COLORS[config.color]} hover:bg-gray-50 transition-colors`}
            >
              <div className="flex items-center gap-2">
                {isCollapsed ? (
                  <ChevronRight className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                )}
                <span className="text-sm font-semibold text-gray-800">{config.label}</span>
                <span className="text-xs text-gray-500 bg-white/70 px-1.5 py-0.5 rounded-full">
                  {items.length}
                </span>
              </div>
            </button>

            {/* Table */}
            {!isCollapsed && (
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 border-t border-gray-200">
                    <th className="text-left px-3 py-2 font-medium text-gray-500 w-16">ID</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-500 w-44">CRF Form / Field</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-500 w-24">Type</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-500 w-16">Severity</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-500 w-16">Timing</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-500">Rule Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map((check) => {
                    const isExpanded = expandedCheckId === check.id;
                    const sevCfg = SEVERITY_CONFIG[check.severity];
                    const timCfg = TIMING_CONFIG[check.timing];

                    return (
                      <tr key={check.id} className="contents">
                        <td colSpan={6} className="p-0">
                          <div
                            onClick={() => handleRowClick(check.id)}
                            className={`grid grid-cols-[4rem_11rem_6rem_4.5rem_4.5rem_1fr] items-center cursor-pointer hover:bg-blue-50/50 transition-colors ${
                              !check.active ? 'opacity-50' : ''
                            } ${isExpanded ? 'bg-blue-50/30' : ''}`}
                          >
                            <span className="px-3 py-2.5 font-mono text-gray-500 text-[11px]">
                              {check.id}
                            </span>
                            <span className="px-3 py-2.5 truncate">
                              <span className="text-gray-800 font-medium">{check.crfForm}</span>
                              <span className="text-gray-400 mx-1">/</span>
                              <span className="text-gray-600 font-mono">{check.fieldName}</span>
                            </span>
                            <span className="px-3 py-2.5 text-gray-600">
                              {CHECK_TYPE_LABELS[check.checkType] || check.checkType}
                              {checkSourceBadge(check) && (
                                <span
                                  className={`block mt-0.5 w-fit px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                    isDefaultRange(check)
                                      ? 'bg-amber-50 text-amber-800'
                                      : isFromDictionary(check) ? 'bg-teal-50 text-teal-700' : 'bg-indigo-50 text-indigo-700'
                                  }`}
                                  title={
                                    isDefaultRange(check)
                                      ? 'A standard default plausibility range, not from the protocol. The sponsor confirms or replaces it.'
                                      : isFromDictionary(check)
                                        ? 'Written from your data dictionary'
                                        : 'Written by a fixed rule from the study facts'
                                  }
                                >
                                  {checkSourceBadge(check)}
                                </span>
                              )}
                            </span>
                            <span className="px-3 py-2.5">
                              <span
                                className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${sevCfg.bgClass}`}
                              >
                                {sevCfg.label}
                              </span>
                            </span>
                            <span className="px-3 py-2.5">
                              <span
                                className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${timCfg.bgClass}`}
                              >
                                {timCfg.label}
                              </span>
                            </span>
                            <span className="px-3 py-2.5 text-gray-700">
                              {truncate(check.rule, 100)}
                            </span>
                          </div>

                          {/* Expanded detail */}
                          {isExpanded && (
                            <EditCheckDetail
                              check={check}
                              onSave={onSaveCheck}
                              onToggleActive={() => onToggleActive(check.id)}
                              onDelete={() => onDeleteCheck(check.id)}
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
}
